import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams, useLocation } from "react-router-dom";
import { last, isEmpty } from "lodash";

import { Button, Divider, Grid, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { RiderContext } from "../../context";
import { StringUtils, Parsers } from "../../util";
import uuidFromUrl from "../../util/strings/uuidFromUrl";
import ReceiptLineItem from "./subcomponents/ReceiptLineItem";

const { formatNumber, getReceiptLabel, dualCurrencyString } = StringUtils;
const { filterPaymentReferenceTypes } = Parsers;

const useStyles = makeStyles((theme) => ({
  divider: {
    padding: 0,
  },
  creditButton: {
    background: theme.palette.background.buttons.strongBlue,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    margin: `${theme.spacing(1)}px 0`,
    "&.Mui-disabled": {
      background: theme.palette.background.disabled,
    },
  },
}));

const Receipt = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { activeReservation, receipt, fetchReceipt } = useContext(RiderContext);
  const location = useLocation();
  const { riderId, reservationId } = useParams();
  const { t } = useTranslation();
  const [riderReceipt, setRiderReceipt] = useState({});

  const durationLongerThanMinimum =
    (riderReceipt?.duration ?? receipt?.duration) / 60 >=
    (receipt?.rate_plan?.min_fleet_charge_minutes ?? 1);

  const hasActivePass = !!receipt?.active_pass;

  useEffect(() => {
    (async () => {
      if (activeReservation && activeReservation?.group_reservation) {
        await fetchReceipt(last(activeReservation?.group_reservation.split("/")));
      }
    })();
  }, []);

  useEffect(() => {
    if (receipt && !isEmpty(receipt) && receipt?.line_items) {
      setRiderReceipt(receipt?.line_items.find((r) => r.reservation_id === reservationId) ?? {});
    }
  }, [receipt]);

  return (
    <Grid item container xs={12} spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {t("Riders.Receipt.Receipt")}
        </Typography>
      </Grid>
      <ReceiptLineItem
        label={t("Riders.Receipt.Scooter")}
        value={String(
          riderReceipt?.vehicle_shortcode ||
            receipt?.vehicle_shortcode ||
            activeReservation?.vehicleCode ||
            "Not Found",
        )}
        isTitle
        justify={null}
      />
      {/* THIS IS CURRENTLY UTC, WE NEED TO CHANGE THIS TO LOCAL
      TIME WHENEVER THE BILLING SERVER STARTS RETURNING THAT!!!
      ALSO, IOS IS USING THE 'CREATED' FIELD, INTERPRETING THAT
      AS GMT, AND THEN TRANSLATING IT TO LOCAL TIME. UGH
      https://superpedestrian.atlassian.net/browse/SP-9913 */}
      {receipt && !isEmpty(receipt) && (
        <>
          <ReceiptLineItem
            label={
              receipt?.trip_date
                ? Intl.DateTimeFormat("en", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    hour12: true,
                  }).format(receipt?.trip_date)
                : "Error processing timestamp"
            }
            value={`${Math.floor((riderReceipt?.duration ?? receipt?.duration) / 60) || 0} min`}
            isTitle
          />
          <ReceiptLineItem
            label={t("Riders.Receipt.Rate")}
            value={
              receipt?.fee_type === "time"
                ? `${formatNumber(
                    receipt?.rate_plan?.time_unit_fee || 0,
                    receipt?.currencyLocal,
                  )}/min`
                : `${formatNumber(
                    receipt?.rate_plan?.distance_unit_fee || 0,
                    receipt?.currencyLocal,
                  )}/km`
            }
          />
          {receipt?.rate_plan?.min_fleet_charge_amount && (
            <ReceiptLineItem
              label={t("Riders.Receipt.MinimumFare")}
              value={`${formatNumber(
                receipt?.rate_plan?.min_fleet_charge_amount,
                receipt?.currencyLocal,
              )} (${receipt?.rate_plan?.min_fleet_charge_minutes} min)`}
            />
          )}

          <Grid item xs={12} className={classes.divider}>
            <Divider />
          </Grid>
          <ReceiptLineItem
            label={t("Riders.Receipt.FareTotal")}
            value={formatNumber(
              riderReceipt?.fareTotalLocal || riderReceipt?.total_amount || receipt?.fareTotalLocal,
              receipt?.currencyLocal,
            )}
            isTitle
          />
          <ReceiptLineItem
            label={t("Riders.Receipt.UnlockFee")}
            value={formatNumber(receipt?.rate_plan?.flat_fee, receipt?.currencyLocal)}
          />
          {receipt?.fees.map((fee) => (
            <ReceiptLineItem
              label={fee.name}
              value={formatNumber(fee.total_amount, receipt?.currencyLocal)}
              key={fee?.id}
            />
          ))}
          {!isEmpty(riderReceipt) &&
            riderReceipt?.fees.map((fee) => (
              <ReceiptLineItem
                label={fee.name}
                value={formatNumber(fee.total_amount, receipt?.currencyLocal)}
                key={fee?.id}
              />
            ))}
          <ReceiptLineItem
            label={t("Riders.Receipt.Fare")}
            value={formatNumber(receipt?.baseFare, receipt?.currencyLocal)}
          />
          {receipt?.active_pass && (
            <ReceiptLineItem
              label={`${t("Riders.Receipt.Pass")} (${
                receipt.active_pass.pass_offer?.pass_offer_type?.offer_type
              } - ${receipt.passDeductionMinutes} ${t("Riders.Receipt.Minutes")})`}
              value={`- ${formatNumber(receipt.passDeductionWallet, receipt?.currencyWallet)}`}
            />
          )}
          {receipt?.coupons?.length > 0 && (
            <ReceiptLineItem
              label={t("Riders.Receipt.Promo")}
              value={`- ${formatNumber(receipt?.discount_amount, receipt?.currencyLocal)}`}
            />
          )}
          {receipt?.coupons.map((coup) => (
            <ReceiptLineItem
              label={
                coup.type === "discount"
                  ? `${t(coup.code)} (${coup.percent_off}% ${coup.type}) - ${StringUtils.convertTz(
                      coup.expired,
                      "UTC",
                      "UTC",
                      "DATE_MED",
                    )}`
                  : `${t(coup.code)} (${coup.type}) - ${StringUtils.convertTz(
                      coup.expired,
                      "UTC",
                      "UTC",
                      "DATE_MED",
                    )}`
              }
              key={coup.code}
            />
          ))}
          <ReceiptLineItem
            label={t("Riders.Receipt.Subtotal")}
            value={formatNumber(
              riderReceipt?.subtotal ?? receipt?.printSubtotal,
              receipt?.currencyLocal,
            )}
            tooltipProps={
              hasActivePass ||
              durationLongerThanMinimum ||
              (!riderReceipt?.subtotal ?? !receipt?.subtotal)
                ? {}
                : { tooltip: t("Riders.Receipt.AdjustedForMinimumFare"), keyTooltip: false }
            }
          />
          {riderReceipt?.tax ? (
            <ReceiptLineItem
              label={`${riderReceipt?.tax?.name} (${riderReceipt?.tax?.rate}%)`}
              value={formatNumber(riderReceipt?.tax_total, receipt?.currencyLocal)}
            />
          ) : (
            receipt?.tax && (
              <ReceiptLineItem
                label={`${receipt?.tax?.name} (${receipt?.tax?.rate}%)`}
                value={formatNumber(receipt?.tax_total, receipt?.currencyLocal)}
              />
            )
          )}
          <Grid item xs={12} className={classes.divider}>
            <Divider />
          </Grid>
          {!activeReservation?.group_reservation && (
            <>
              <ReceiptLineItem label={t("Riders.Receipt.Charged")} isTitle />
              {filterPaymentReferenceTypes(receipt?.payments).map((payment) => (
                <ReceiptLineItem
                  label={!isEmpty(payment) && getReceiptLabel(payment, t)}
                  value={formatNumber(
                    riderReceipt?.total_amount || payment?.totalAmount,
                    receipt?.currencyWallet || receipt?.currencyLocal,
                  )}
                  key={payment?.label + payment?.reference_type + payment?.status}
                  labelColor={payment?.labelColor}
                  strikeThrough={payment?.strikeThrough}
                />
              ))}
              {receipt?.forgiven_amount && (
                <ReceiptLineItem
                  label={t("Riders.Receipt.AmountForgiven")}
                  value={formatNumber(receipt?.forgiven_amount, receipt?.currencyLocal)}
                />
              )}
              <Grid item xs={12} className={classes.divider}>
                <Divider />
              </Grid>
              <ReceiptLineItem label={t("Riders.Receipt.BalanceDue")} isTitle />
              {receipt?.parkingIncentiveBonusWallet != null && (
                <ReceiptLineItem
                  label={t("Riders.Receipt.ParkingIncentive")}
                  value={`+ ${dualCurrencyString(
                    receipt?.exchange_rate,
                    receipt?.parkingIncentiveBonusWallet,
                    receipt?.currencyWallet,
                    receipt?.parkingIncentiveBonusLocal,
                    receipt?.currencyLocal,
                  )}`}
                />
              )}
              <ReceiptLineItem
                label={t("Riders.Receipt.PreviousWalletBalance")}
                value={dualCurrencyString(
                  receipt?.exchange_rate,
                  receipt?.beforeWalletBalance,
                  receipt?.currencyWallet,
                  receipt?.exchange_rate?.balance?.before,
                  receipt?.currencyLocal,
                )}
              />
              <ReceiptLineItem
                label={t("Riders.Receipt.NewWalletBalance")}
                value={dualCurrencyString(
                  receipt?.exchange_rate,
                  receipt?.afterWalletBalance,
                  receipt?.currencyWallet,
                  receipt?.exchange_rate?.balance?.after,
                  receipt?.currencyLocal,
                )}
              />
            </>
          )}
        </>
      )}
      <Grid item container xs={12}>
        {!activeReservation?.group_reservation ? (
          <Button
            component={RouterLink}
            className={classes.creditButton}
            disabled={!receipt?.total_amount}
            to={{
              pathname: `/riders/${riderId}/reservations/${reservationId}/credit-wallet`,
              state: { background: location },
            }}
          >
            {t("Riders.Receipt.CreditToWallet")}
          </Button>
        ) : (
          <Button
            component={RouterLink}
            className={classes.creditButton}
            to={{
              pathname: `/riders/${riderId}/group-reservations/${uuidFromUrl(
                activeReservation.group_reservation,
              )}`,
            }}
          >
            {t("Riders.Receipt.ViewGroupDetail")}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default Receipt;
